<template>
  <li @mouseenter="decidePosition" v-safe-class="submenuPosition">
    <input type="radio" :name="menu.group" :checked="viewModel.checked" class="hidden" v-if="isCheckable" />
    <a class="dropdown-item" :class="{ disabled: !enabled }" href="#" @click.prevent="handleClick">
      <i :class="menu?.icon" v-if="menu.icon"></i>
      <span>{{ menu?.text }}</span>
    </a>
    <ul class="dropdown-menu" v-if="children?.length">
      <Dropdown-child-menu-item v-for="(entry, index) in children" :item="entry" :key="index" @selected="$emit('selected', $event)"></Dropdown-child-menu-item>
    </ul>
  </li>
</template>
<script lang="ts" setup>
import { DropdownMenuItem, DropdownSubMenuPosition, MenuType } from "@medapro-types/common/components/DropdownMenu";
import { computed, reactive, watch } from "vue";

const props = defineProps<{ item: DropdownMenuItem }>();

const menu = computed(() => props.item);

const emit = defineEmits(["selected", "change"]);

const viewModel = reactive({
  checked: false,
  position: "right" as DropdownSubMenuPosition | undefined
});

const enabled = computed(() => {
  if (!menu.value) {
    return false;
  }

  if (menu.value.enabled === null || menu.value.enabled === undefined) {
    return true;
  }

  if (typeof menu.value.enabled === "boolean") {
    return menu.value.enabled;
  }

  return menu.value.enabled(menu.value);
});

const children = computed(() => menu.value?.children || []);

const submenuPosition = computed(() => {
  if (!menu.value) {
    return "";
  }

  const menuItems = [
    menu.value.subMenuPosition || viewModel.position || "",
    children.value?.length ? " nested-menu" : "",
    isCheckable.value && viewModel.checked ? "checked" : "",
    isCheckable.value ? (menu.value.type === MenuType.CheckboxMenu ? "checkbox-menu" : "radiobutton-menu") : "",
    menu.value.separator === "top" ? "separator-before" : "",
    menu.value.separator === "bottom" ? "separator-after" : ""
  ];

  return menuItems.filter((x) => x);
});

const isCheckable = computed(() => menu.value && (menu.value.type === MenuType.CheckboxMenu || menu.value.type === MenuType.RadiobuttonMenu));

watch(
  () => menu.value?.checked,
  (nv) => {
    if (menu.value?.type !== MenuType.DefaultMenu) {
      viewModel.checked = nv === true;
    } else {
      viewModel.checked = false;
    }
  },
  {
    immediate: true
  }
);

watch(
  () => viewModel.checked,
  (nv) => {
    if (isCheckable.value) {
      setTimeout(() => {
        if (menu.value.changeHandler) {
          menu.value.changeHandler(nv, menu.value);
          emit("change", nv);
        }
      }, 2);
    }
  }
);

function handleClick() {
  // in case of radiobutton menu, a re-checked item does not change anything
  if (menu.value?.type === MenuType.RadiobuttonMenu && viewModel.checked) {
    emit("selected", viewModel.checked);
    return;
  }

  if (menu.value && enabled.value) {
    if (isCheckable.value) {
      viewModel.checked = !viewModel.checked;
    }

    //only pass the state when this menu is checkable.
    const checkedState = isCheckable.value ? viewModel.checked : undefined;
    emit("selected", checkedState);

    setTimeout(() => {
      if (menu.value.clickHandler) {
        menu.value.clickHandler(menu.value, checkedState);
      }
    }, 2);
  }
}

function decidePosition(event: MouseEvent) {
  const element: HTMLElement = event.target as HTMLElement;
  const subMenu: HTMLElement = element.querySelector("ul:first-of-type") as HTMLElement;
  if (subMenu) {
    const left = element.getBoundingClientRect().right;
    const width = subMenu.clientWidth;
    const parent: HTMLElement | null = getScrollParent(subMenu);

    const isEntirelyVisible = left + width <= (parent?.clientWidth || window.innerWidth);

    if (isEntirelyVisible) {
      viewModel.position = "right";
    } else {
      viewModel.position = "left";
    }
  } else {
    viewModel.position = undefined;
  }
}

function getScrollParent(node: HTMLElement): HTMLElement | null {
  const isElement = node instanceof HTMLElement;
  if (!isElement) {
    return null;
  }

  const overflowY: string = window.getComputedStyle(node).overflowY;
  const isScrollable = !(overflowY.includes("hidden") || overflowY.includes("visible"));

  if (!node) {
    return null;
  } else if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node;
  } else if (!node.parentNode) {
    return (document.scrollingElement || document.body) as HTMLElement;
  }

  return (getScrollParent(node.parentNode as HTMLElement) || document.scrollingElement || document.body) as HTMLElement;
}
</script>
<style></style>
